import { useCallback, useEffect, useMemo, useState } from 'react'
import Swal from 'sweetalert2'
import { useTranslation } from 'react-i18next'
import TrainingAddTrainerModal from './TrainingAddTrainerModal'
import TrainingTrainerExtraCosts from './TrainingTrainerExtraCosts'
import {
	applyRecalculatedDoWyplaty,
	buildTrainerOptionRows,
	calculateDoWyplaty,
	calculateDoWyplatyDojazdy,
	calculateDoWyplatyGodziny,
	calculateDoWyplatyInne,
	createTrainerExtraCost,
	formatMoneyPln,
	normalizeTrainerExtraCosts,
	parseTrainingDurationHours,
	serializeTrainerExtraCosts,
	sortTrainerOptions,
} from './trainerDetailsHelpers'

const inputClassName = 'block w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500'
const readOnlyValueClassName = 'flex min-h-[42px] items-center rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-medium text-gray-900'

const createDraftRows = (trainerRows, training) => (Array.isArray(trainerRows) ? trainerRows : []).map(row => ({
	...row,
	odleglosc_km: row.odleglosc_km ?? '',
	stawka_godzinowa: row.stawka_godzinowa ?? '',
	stawka_za_kilometr: row.stawka_za_kilometr ?? 1,
	ilosc_godzin: row.ilosc_godzin ?? parseTrainingDurationHours(training) ?? '',
	do_wyplaty: row.do_wyplaty !== '' && row.do_wyplaty != null
		? row.do_wyplaty
		: (calculateDoWyplaty(row, training) ?? ''),
	rozliczono: Boolean(row.rozliczono),
	inne_koszty: normalizeTrainerExtraCosts(row.inne_koszty),
}))

const rowsAreEqual = (leftRows, rightRows) => {
	if (leftRows.length !== rightRows.length) return false

	return leftRows.every((leftRow, index) => {
		const rightRow = rightRows[index]
		return leftRow.key === rightRow.key
			&& `${leftRow.odleglosc_km ?? ''}` === `${rightRow.odleglosc_km ?? ''}`
			&& `${leftRow.stawka_godzinowa ?? ''}` === `${rightRow.stawka_godzinowa ?? ''}`
			&& `${leftRow.stawka_za_kilometr ?? ''}` === `${rightRow.stawka_za_kilometr ?? ''}`
			&& `${leftRow.ilosc_godzin ?? ''}` === `${rightRow.ilosc_godzin ?? ''}`
			&& `${leftRow.do_wyplaty ?? ''}` === `${rightRow.do_wyplaty ?? ''}`
			&& Boolean(leftRow.rozliczono) === Boolean(rightRow.rozliczono)
			&& serializeTrainerExtraCosts(leftRow.inne_koszty) === serializeTrainerExtraCosts(rightRow.inne_koszty)
	})
}

export default function TrainingAssignedTrainersSection({
	training,
	trainerRows,
	certificationOptions,
	onAddTrainer,
	onRemoveTrainer,
	onRefreshTrainers,
	onSaveAssignedTrainers,
}) {
	const { t } = useTranslation()
	const [saving, setSaving] = useState(false)
	const [loadingTrainerOptions, setLoadingTrainerOptions] = useState(false)
	const [modalTrainerSource, setModalTrainerSource] = useState(null)
	const [isAddTrainerModalOpen, setIsAddTrainerModalOpen] = useState(false)
	const [trainerSearchTerm, setTrainerSearchTerm] = useState('')
	const [trainerSortColumn, setTrainerSortColumn] = useState('distanceKm')
	const [trainerSortDirection, setTrainerSortDirection] = useState('asc')
	const [trainerOptionsLimit, setTrainerOptionsLimit] = useState(5)
	const [draftRows, setDraftRows] = useState(() => createDraftRows(trainerRows, training))

	useEffect(() => {
		setDraftRows(createDraftRows(trainerRows, training))
	}, [trainerRows, training])

	const savedRows = useMemo(() => createDraftRows(trainerRows, training), [trainerRows, training])
	const hasUnsavedChanges = useMemo(
		() => !rowsAreEqual(draftRows, savedRows),
		[draftRows, savedRows]
	)

	const certificationMap = useMemo(() => new Map(
		(Array.isArray(certificationOptions) ? certificationOptions : []).map(item => [item.value, item.label || '—'])
	), [certificationOptions])

	const getMissingCertificationNames = useCallback((trainerItem, requiredCertificationIds) => {
		const normalizedRequiredCertificationIds = Array.isArray(requiredCertificationIds)
			? requiredCertificationIds.filter(Boolean)
			: []

		return normalizedRequiredCertificationIds
			.filter(certificationId => !(Array.isArray(trainerItem?.certificationIds) && trainerItem.certificationIds.includes(certificationId)))
			.map(certificationId => certificationMap.get(certificationId) || certificationId)
	}, [certificationMap])

	const assignedTrainerIds = useMemo(
		() => new Set(trainerRows.map(item => item.trainerId).filter(Boolean)),
		[trainerRows]
	)

	const trainerOptionsSource = modalTrainerSource

	const availableTrainers = useMemo(() => (
		Array.isArray(trainerOptionsSource) ? trainerOptionsSource : []
	).filter(trainerItem => !assignedTrainerIds.has(trainerItem._id || trainerItem.id)), [
		assignedTrainerIds,
		trainerOptionsSource,
	])

	const trainerOptions = useMemo(() => buildTrainerOptionRows({
		availableTrainers,
		certificationMap,
		getMissingCertificationNames,
		requiredCertificationIds: training?.requiredCertificationIds,
	}), [availableTrainers, certificationMap, getMissingCertificationNames, training?.requiredCertificationIds])

	const filteredTrainerOptions = useMemo(() => {
		const query = `${trainerSearchTerm || ''}`.trim().toLowerCase()
		if (!query) return trainerOptions

		return trainerOptions.filter(option => {
			const haystack = [
				option.trainerName,
				option.miasto,
				...(Array.isArray(option.certificationNames) ? option.certificationNames : []),
				...(Array.isArray(option.missingCertificationNames) ? option.missingCertificationNames : []),
			]
				.filter(Boolean)
				.join(' ')
				.toLowerCase()

			return haystack.includes(query)
		})
	}, [trainerOptions, trainerSearchTerm])

	const sortedTrainerOptions = useMemo(
		() => sortTrainerOptions(filteredTrainerOptions, trainerSortColumn, trainerSortDirection),
		[filteredTrainerOptions, trainerSortColumn, trainerSortDirection]
	)

	const visibleTrainerOptions = useMemo(
		() => sortedTrainerOptions.slice(0, trainerOptionsLimit),
		[sortedTrainerOptions, trainerOptionsLimit]
	)

	const trainerOptionsHasMore = trainerOptionsLimit < sortedTrainerOptions.length

	const updateRowDraft = useCallback((rowKey, updater, { recalculatePayout = true } = {}) => {
		setDraftRows(prev => prev.map(row => {
			if (row.key !== rowKey) return row

			const nextRow = updater(row)
			return recalculatePayout ? applyRecalculatedDoWyplaty(nextRow, training) : nextRow
		}))
	}, [training])

	const updateDraftField = useCallback((rowKey, field, value) => {
		updateRowDraft(rowKey, row => ({ ...row, [field]: value }), {
			recalculatePayout: field !== 'do_wyplaty' && field !== 'rozliczono',
		})
	}, [updateRowDraft])

	const handleAddExtraCost = useCallback(rowKey => {
		updateRowDraft(rowKey, row => ({
			...row,
			inne_koszty: [...normalizeTrainerExtraCosts(row.inne_koszty), createTrainerExtraCost()],
		}))
	}, [updateRowDraft])

	const handleExtraCostChange = useCallback((rowKey, costId, field, value) => {
		updateRowDraft(rowKey, row => ({
			...row,
			inne_koszty: normalizeTrainerExtraCosts(row.inne_koszty).map(item =>
				item._id === costId ? { ...item, [field]: value } : item
			),
		}))
	}, [updateRowDraft])

	const handleRemoveExtraCost = useCallback((rowKey, costId) => {
		updateRowDraft(rowKey, row => ({
			...row,
			inne_koszty: normalizeTrainerExtraCosts(row.inne_koszty).filter(item => item._id !== costId),
		}))
	}, [updateRowDraft])

	const handleOpenAddTrainerModal = useCallback(async () => {
		if (saving || loadingTrainerOptions) return

		setLoadingTrainerOptions(true)
		const refreshResult = typeof onRefreshTrainers === 'function'
			? await onRefreshTrainers()
			: { success: false, error: t('trainings.details.messages.loadFailed'), data: [] }
		setLoadingTrainerOptions(false)

		if (!refreshResult.success) {
			await Swal.fire(
				t('notifications.errorTitle'),
				refreshResult.error || t('trainings.details.messages.loadFailed'),
				'error'
			)
			return
		}

		const refreshedTrainers = Array.isArray(refreshResult.data) ? refreshResult.data : []
		const nextOptions = buildTrainerOptionRows({
			availableTrainers: refreshedTrainers.filter(trainerItem =>
				!assignedTrainerIds.has(trainerItem._id || trainerItem.id)
			),
			certificationMap,
			getMissingCertificationNames,
			requiredCertificationIds: training?.requiredCertificationIds,
		})

		if (!nextOptions.length) {
			await Swal.fire(
				t('notifications.errorTitle'),
				t('trainings.details.messages.noAvailableTrainers'),
				'error'
			)
			return
		}

		setModalTrainerSource(refreshedTrainers)
		setTrainerSearchTerm('')
		setTrainerOptionsLimit(5)
		setIsAddTrainerModalOpen(true)
	}, [
		assignedTrainerIds,
		certificationMap,
		getMissingCertificationNames,
		loadingTrainerOptions,
		onRefreshTrainers,
		saving,
		t,
		training?.requiredCertificationIds,
	])

	const handleCloseAddTrainerModal = useCallback(() => {
		if (saving) return
		setModalTrainerSource(null)
		setTrainerSearchTerm('')
		setTrainerOptionsLimit(5)
		setIsAddTrainerModalOpen(false)
	}, [saving])

	const handleTrainerSearchChange = useCallback(value => {
		setTrainerSearchTerm(value)
		setTrainerOptionsLimit(5)
	}, [])

	const handleSortTrainerOptions = useCallback(column => {
		setTrainerSortDirection(prevDirection =>
			trainerSortColumn === column && prevDirection === 'asc' ? 'desc' : 'asc'
		)
		setTrainerSortColumn(column)
	}, [trainerSortColumn])

	const handleLoadMoreTrainerOptions = useCallback(() => {
		if (!trainerOptionsHasMore) return
		setTrainerOptionsLimit(prev => prev + 5)
	}, [trainerOptionsHasMore])

	const handleAddTrainer = useCallback(async newTrainer => {
		if (saving || !newTrainer) return

		setSaving(true)
		const result = await onAddTrainer(newTrainer)
		setSaving(false)

		if (!result.success) {
			await Swal.fire(
				t('notifications.errorTitle'),
				result.error || t('trainings.details.messages.updateTrainersFailed'),
				'error'
			)
			return
		}

		setModalTrainerSource(null)
		setIsAddTrainerModalOpen(false)
	}, [onAddTrainer, saving, t])

	const handleSaveChanges = useCallback(async () => {
		if (saving || !hasUnsavedChanges) return

		setSaving(true)
		const result = typeof onSaveAssignedTrainers === 'function'
			? await onSaveAssignedTrainers(draftRows)
			: { success: false, error: t('trainings.details.messages.updateTrainersFailed') }
		setSaving(false)

		if (!result.success) {
			await Swal.fire(
				t('notifications.errorTitle'),
				result.error || t('trainings.details.messages.updateTrainersFailed'),
				'error'
			)
			return
		}

		await Swal.fire(
			t('notifications.successTitle'),
			t('trainings.details.messages.trainersSavedSuccess'),
			'success'
		)
	}, [draftRows, hasUnsavedChanges, onSaveAssignedTrainers, saving, t])

	const handleRemove = async trainerRow => {
		if (saving) return

		const confirm = await Swal.fire({
			title: t('trainings.details.deleteTrainer.confirmTitle'),
			text: t('trainings.details.deleteTrainer.confirmText', { name: trainerRow.trainerName || '—' }),
			icon: 'warning',
			showCancelButton: true,
			confirmButtonText: t('trainings.details.actions.removeTrainer'),
			cancelButtonText: t('notifications.cancelButton'),
			confirmButtonColor: '#dc2626',
		})

		if (!confirm.isConfirmed) return

		setSaving(true)
		const result = typeof onRemoveTrainer === 'function'
			? await onRemoveTrainer(trainerRow)
			: { success: false, error: t('trainings.details.messages.updateTrainersFailed') }
		setSaving(false)

		if (!result.success) {
			await Swal.fire(
				t('notifications.errorTitle'),
				result.error || t('trainings.details.messages.updateTrainersFailed'),
				'error'
			)
			return
		}

		await Swal.fire(
			t('notifications.successTitle'),
			t('trainings.details.messages.trainerRemovedSuccess', { name: trainerRow.trainerName || '—' }),
			'success'
		)
	}

	return (
		<div className="rounded-md border border-gray-200 bg-white px-5 py-4 shadow-sm">
			<div className="flex items-center justify-between gap-3">
				<h2 className="text-sm font-semibold text-gray-900">{t('trainings.details.sections.trainers')}</h2>
				<button
					type="button"
					onClick={handleOpenAddTrainerModal}
					disabled={saving || loadingTrainerOptions}
					className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
				>
					{t('trainings.details.actions.addTrainer')}
				</button>
			</div>

			<div className="mt-4 space-y-3">
				{draftRows.length
					? draftRows.map(trainerRow => {
						const doWyplatyGodziny = calculateDoWyplatyGodziny(trainerRow, training)
						const doWyplatyDojazdy = calculateDoWyplatyDojazdy(trainerRow)
						const doWyplatyInne = calculateDoWyplatyInne(trainerRow)

						return (
							<div
								key={trainerRow.key}
								className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm"
							>
								<div className="flex items-start justify-between gap-3">
									<div className="min-w-0">
										<p className="text-sm font-semibold text-gray-900">{trainerRow.trainerName || '—'}</p>
										{trainerRow.miasto
											? <p className="mt-0.5 text-sm text-gray-500">{trainerRow.miasto}</p>
											: null}
									</div>

									<button
										type="button"
										onClick={() => handleRemove(trainerRow)}
										disabled={saving}
										className="shrink-0 rounded-md border border-red-200 bg-white px-3 py-2 text-sm font-semibold text-red-700 shadow-sm hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
									>
										{t('trainings.details.actions.removeTrainer')}
									</button>
								</div>

								<div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
									<label className="flex flex-col gap-1">
										<span className="text-xs font-medium uppercase tracking-wide text-gray-500">
											{t('trainings.details.trainerDialog.table.hourlyRateGross')}
										</span>
										<input
											type="number"
											min="0"
											step="0.01"
											value={trainerRow.stawka_godzinowa ?? ''}
											onChange={event => updateDraftField(trainerRow.key, 'stawka_godzinowa', event.target.value)}
											disabled={saving}
											className={inputClassName}
										/>
									</label>

									<label className="flex flex-col gap-1">
										<span className="text-xs font-medium uppercase tracking-wide text-gray-500">
											{t('trainings.details.trainerDialog.fields.hoursCount')}
										</span>
										<input
											type="number"
											min="0"
											step="0.01"
											value={trainerRow.ilosc_godzin ?? ''}
											onChange={event => updateDraftField(trainerRow.key, 'ilosc_godzin', event.target.value)}
											disabled={saving}
											className={inputClassName}
										/>
									</label>

									<div className="flex flex-col gap-1">
										<span className="text-xs font-medium uppercase tracking-wide text-gray-500">
											{t('trainings.details.trainerDialog.fields.doWyplatyGodziny')}
										</span>
										<div className={readOnlyValueClassName}>
											{doWyplatyGodziny == null ? '—' : formatMoneyPln(doWyplatyGodziny)}
										</div>
									</div>

									<label className="flex flex-col gap-1">
										<span className="text-xs font-medium uppercase tracking-wide text-gray-500">
											{t('trainings.details.trainerDialog.table.distance')}
										</span>
										<input
											type="number"
											min="0"
											step="0.1"
											value={trainerRow.odleglosc_km ?? ''}
											onChange={event => updateDraftField(trainerRow.key, 'odleglosc_km', event.target.value)}
											disabled={saving}
											className={inputClassName}
										/>
									</label>

									<label className="flex flex-col gap-1">
										<span className="text-xs font-medium uppercase tracking-wide text-gray-500">
											{t('trainings.details.trainerDialog.fields.ratePerKm')}
										</span>
										<input
											type="number"
											min="0"
											step="0.01"
											value={trainerRow.stawka_za_kilometr ?? 1}
											onChange={event => updateDraftField(trainerRow.key, 'stawka_za_kilometr', event.target.value)}
											disabled={saving}
											className={inputClassName}
										/>
									</label>

									<div className="flex flex-col gap-1">
										<span className="text-xs font-medium uppercase tracking-wide text-gray-500">
											{t('trainings.details.trainerDialog.fields.doWyplatyDojazdy')}
										</span>
										<div className={readOnlyValueClassName}>
											{doWyplatyDojazdy == null ? '—' : formatMoneyPln(doWyplatyDojazdy)}
										</div>
									</div>

									<div className="sm:col-span-2 lg:col-span-3">
										<TrainingTrainerExtraCosts
											extraCosts={trainerRow.inne_koszty}
											disabled={saving}
											onAdd={() => handleAddExtraCost(trainerRow.key)}
											onChange={(costId, field, value) => handleExtraCostChange(trainerRow.key, costId, field, value)}
											onRemove={costId => handleRemoveExtraCost(trainerRow.key, costId)}
										/>
									</div>

									<div className="flex flex-col gap-1">
										<span className="text-xs font-medium uppercase tracking-wide text-gray-500">
											{t('trainings.details.trainerDialog.fields.doWyplatyInne')}
										</span>
										<div className={readOnlyValueClassName}>
											{doWyplatyInne == null ? '—' : formatMoneyPln(doWyplatyInne)}
										</div>
									</div>

									<label className="flex flex-col gap-1">
										<span className="text-xs font-medium uppercase tracking-wide text-gray-500">
											{t('trainings.details.trainerDialog.fields.doWyplaty')}
										</span>
										<input
											type="number"
											min="0"
											step="0.01"
											value={trainerRow.do_wyplaty ?? ''}
											onChange={event => updateDraftField(trainerRow.key, 'do_wyplaty', event.target.value)}
											disabled={saving}
											className={inputClassName}
										/>
									</label>

									<label className="flex items-end gap-2 pb-2">
										<input
											type="checkbox"
											checked={trainerRow.rozliczono === true}
											onChange={event => updateDraftField(trainerRow.key, 'rozliczono', event.target.checked)}
											disabled={saving}
											className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
										/>
										<span className="text-sm font-medium text-gray-700">
											{t('trainings.details.trainerDialog.fields.rozliczono')}
										</span>
									</label>
								</div>
							</div>
						)
					})
					: <span className="text-sm text-gray-500">{t('trainings.details.emptyTrainers')}</span>}
			</div>

			{draftRows.length > 0 && (
				<div className="mt-4 flex justify-end">
					<button
						type="button"
						onClick={handleSaveChanges}
						disabled={saving || !hasUnsavedChanges}
						className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
					>
						{saving
							? t('trainings.details.actions.savingTrainer')
							: t('trainings.details.actions.saveChanges')}
					</button>
				</div>
			)}

			<TrainingAddTrainerModal
				isOpen={isAddTrainerModalOpen}
				onClose={handleCloseAddTrainerModal}
				onSubmit={handleAddTrainer}
				isSaving={saving}
				onSearchChange={handleTrainerSearchChange}
				hasMore={trainerOptionsHasMore}
				onLoadMore={handleLoadMoreTrainerOptions}
				onSort={handleSortTrainerOptions}
				searchTerm={trainerSearchTerm}
				sortColumn={trainerSortColumn}
				sortDirection={trainerSortDirection}
				totalCount={sortedTrainerOptions.length}
				trainerOptions={visibleTrainerOptions}
			/>
		</div>
	)
}
